import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase, checkConnection, recoverConnection, withTimeout } from '../lib/supabase';
import { Button } from '../components/ui/Button';
import { Mail, AlertCircle, Wifi, WifiOff, Loader2, CheckCircle, RefreshCw } from 'lucide-react';
import { useTranslation } from '../lib/i18n';

const Auth = () => {
  const { t } = useTranslation();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isSignUp, setIsSignUp] = useState(false);
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [isRecovering, setIsRecovering] = useState(false);
  const [signUpSuccess, setSignUpSuccess] = useState(false);
  const [connectionStatus, setConnectionStatus] = useState<'checking' | 'connected' | 'error'>('checking');
  const navigate = useNavigate();

  const checkServerConnection = async () => {
    setConnectionStatus('checking');
    const isConnected = await checkConnection(true);
    setConnectionStatus(isConnected ? 'connected' : 'error');
    return isConnected;
  };

  useEffect(() => {
    const handleOnline = async () => {
      setIsOnline(true);
      setIsRecovering(true);
      const recovered = await recoverConnection();
      if (!recovered) {
        setError(t('auth.unableReconnect'));
        setConnectionStatus('error');
      } else {
        setConnectionStatus('connected');
        setError(null);
      }
      setIsRecovering(false);
    };

    const handleOffline = () => {
      setIsOnline(false);
      setError(t('auth.offlineMessage'));
      setConnectionStatus('error');
    };

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    // Initial connection check
    checkServerConnection();

    // Check for existing session
    supabase.auth.getSession().then(({ data: { session } }) => {
      if (session) {
        navigate('/');
      }
    });

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, [navigate]);

  const validateEmail = (email: string) => {
    const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
    return emailRegex.test(email.toLowerCase());
  };

  const handleRetryConnection = async () => {
    setError(null);
    setIsRecovering(true);
    const isConnected = await checkServerConnection();
    setIsRecovering(false);
    
    if (!isConnected) {
      setError(t('auth.stillUnable'));
    }
  };

  const handleAuth = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!isOnline) {
      setError(t('auth.checkInternet'));
      return;
    }

    if (!email || !password) {
      setError(t('auth.enterBoth'));
      return;
    }

    if (!validateEmail(email)) {
      setError(t('auth.enterValidEmail'));
      return;
    }

    setLoading(true);
    setError(null);
    setSignUpSuccess(false);

    try {
      // Check connection before attempting authentication
      const isConnected = await checkServerConnection();
      if (!isConnected) {
        throw new Error(t('auth.unableConnectServer'));
      }

      const normalizedEmail = email.toLowerCase().trim();

      if (isSignUp) {
        const { data, error: signUpError } = await withTimeout(
          supabase.auth.signUp({
            email: normalizedEmail,
            password,
            options: {
              emailRedirectTo: `${window.location.origin}/auth/callback`
            }
          }),
          10000 // 10 second timeout
        );

        if (signUpError) {
          if (signUpError.message.includes('already registered')) {
            throw new Error(t('auth.accountExists'));
          }
          throw signUpError;
        }

        if (!data?.user) {
          throw new Error(t('auth.failedCreate'));
        }

        setSignUpSuccess(true);
        setEmail('');
        setPassword('');

      } else {
        const { data, error: signInError } = await withTimeout(
          supabase.auth.signInWithPassword({
            email: normalizedEmail,
            password,
          }),
          10000 // 10 second timeout
        );

        if (signInError) {
          if (signInError.message.includes('Invalid login credentials')) {
            throw new Error(t('auth.invalidCredentials'));
          } else if (signInError.message.includes('Email not confirmed')) {
            throw new Error(t('auth.emailNotConfirmed'));
          }
          throw signInError;
        }

        if (data?.session) {
          navigate('/');
        } else {
          throw new Error(t('auth.failedSignIn'));
        }
      }
    } catch (err: any) {
      console.error('Auth error:', err);
      if (err.message?.includes('timed out') || err.message?.includes('timeout')) {
        setError(t('auth.requestTimeout'));
      } else {
        setError(err.message || t('auth.unexpectedError'));
      }
      setSignUpSuccess(false);
      
      // If it's a connection error, update the connection status
      if (err.message.includes('Unable to connect') || err.message.includes('timed out')) {
        setConnectionStatus('error');
      }
    } finally {
      setLoading(false);
    }
  };

  const getConnectionStatusColor = () => {
    switch (connectionStatus) {
      case 'connected':
        return 'bg-green-100 dark:bg-green-900/50 border-green-200 dark:border-green-800 text-green-700 dark:text-green-200';
      case 'error':
        return 'bg-red-100 dark:bg-red-900/50 border-red-200 dark:border-red-800 text-red-700 dark:text-red-200';
      default:
        return 'bg-blue-100 dark:bg-blue-900/50 border-blue-200 dark:border-blue-800 text-blue-700 dark:text-blue-200';
    }
  };

  return (
    <div className="max-w-md mx-auto mt-16">
      <div className="bg-white dark:bg-gray-800 p-8 rounded-lg shadow-md">
        <h2 className="text-2xl font-bold mb-6 text-center">
          {isSignUp ? t('auth.createAccount') : t('auth.signIn')}
        </h2>

        {!isOnline && (
          <div className="flex items-center gap-2 bg-yellow-100 dark:bg-yellow-900/50 border border-yellow-200 dark:border-yellow-800 text-yellow-700 dark:text-yellow-200 px-4 py-3 rounded mb-4">
            <WifiOff className="h-4 w-4 flex-shrink-0" />
            <p className="text-sm">{t('auth.offline')}</p>
          </div>
        )}

        {connectionStatus === 'error' && (
          <div className={`flex items-center justify-between gap-2 ${getConnectionStatusColor()} px-4 py-3 rounded mb-4`}>
            <div className="flex items-center gap-2">
              <AlertCircle className="h-4 w-4 flex-shrink-0" />
              <p className="text-sm">{t('auth.connectionError')}</p>
            </div>
            <Button
              onClick={handleRetryConnection}
              disabled={loading || isRecovering}
              className="!p-1 !h-8 !min-w-8"
              variant="ghost"
            >
              <RefreshCw className={`h-4 w-4 ${isRecovering ? 'animate-spin' : ''}`} />
            </Button>
          </div>
        )}

        {error && !error.includes('Unable to connect') && !error.includes('Connection error') && (
          <div className="flex items-center gap-2 bg-red-100 dark:bg-red-900/50 border border-red-200 dark:border-red-800 text-red-700 dark:text-red-200 px-4 py-3 rounded mb-4">
            <AlertCircle className="h-4 w-4 flex-shrink-0" />
            <p className="text-sm">{error}</p>
          </div>
        )}

        {signUpSuccess && (
          <div className="flex items-center gap-2 bg-green-100 dark:bg-green-900/50 border border-green-200 dark:border-green-800 text-green-700 dark:text-green-200 px-4 py-3 rounded mb-4">
            <CheckCircle className="h-4 w-4 flex-shrink-0" />
            <div>
              <p className="text-sm font-medium">{t('auth.accountCreated')}</p>
              <p className="text-xs mt-1">{t('auth.checkEmail')}</p>
            </div>
          </div>
        )}

        {isRecovering && (
          <div className="flex items-center gap-2 bg-blue-100 dark:bg-blue-900/50 border border-blue-200 dark:border-blue-800 text-blue-700 dark:text-blue-200 px-4 py-3 rounded mb-4">
            <Loader2 className="h-4 w-4 flex-shrink-0 animate-spin" />
            <p className="text-sm">{t('auth.reconnecting')}</p>
          </div>
        )}

        <form onSubmit={handleAuth} className="space-y-4">
          <div>
            <label className="block text-sm font-medium mb-1" htmlFor="email">
              {t('auth.email')}
            </label>
            <input
              id="email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="w-full p-2 border rounded focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:border-gray-600"
              required
              disabled={loading || isRecovering}
              autoComplete="email"
            />
          </div>

          <div>
            <label className="block text-sm font-medium mb-1" htmlFor="password">
              {t('auth.password')}
            </label>
            <input
              id="password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="w-full p-2 border rounded focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:border-gray-600"
              required
              disabled={loading || isRecovering}
              autoComplete={isSignUp ? 'new-password' : 'current-password'}
              minLength={6}
            />
          </div>

          <Button
            type="submit"
            className="w-full"
            disabled={loading || !isOnline || isRecovering || connectionStatus === 'error'}
          >
            {loading || isRecovering ? (
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            ) : isOnline ? (
              <Mail className="w-4 h-4 mr-2" />
            ) : (
              <WifiOff className="w-4 h-4 mr-2" />
            )}
            {loading ? t('auth.pleaseWait') : 
             isRecovering ? t('auth.reconnectingAction') :
             isSignUp ? t('common.signUp') : t('common.signIn')}
          </Button>
        </form>

        <div className="mt-4 text-center">
          <button
            onClick={() => {
              setIsSignUp(!isSignUp);
              setError(null);
              setSignUpSuccess(false);
              setEmail('');
              setPassword('');
            }}
            className="text-blue-600 hover:underline dark:text-blue-400"
            type="button"
            disabled={loading || isRecovering}
          >
            {isSignUp
              ? t('auth.haveAccount')
              : t('auth.noAccount')}
          </button>
        </div>
      </div>
    </div>
  );
};

export default Auth;